import { Component, OnInit } from '@angular/core';
import { Router, RouterLink } from "@angular/router";
import { ClrDatagridModule, ClrDatagridSortOrder, ClrIconModule } from "@clr/angular";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { NgIf } from "@angular/common";
import { ClarityIcons, floppyIcon, infoCircleIcon, pencilIcon, timesIcon, twoWayArrowsIcon } from "@cds/core/icon";
import { AlertService, AuthService, IAlertType, StaffStoreService } from "../../../_shared/services";
import { AuthUser, Staff, WebserviceResponse } from "../../../_shared/interfaces";
import { EditStaffComponent } from "./edit-staff/edit-staff.component";
import { of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";

@Component({
  selector: 'app-staff',
  standalone: true,
  imports: [
    RouterLink,
    ClrIconModule,
    ClrDatagridModule,
    ReactiveFormsModule,
    NgIf,
    EditStaffComponent
  ],
  templateUrl: './staff.component.html',
  styleUrl: './staff.component.sass'
})
export class StaffComponent implements OnInit {
  staff: Staff[] = [];
  authUsersByStaffId: Record<number, AuthUser> = {};
  isAdmin = false;
  loading = true;
  editMode = false;
  updating = false;
  updateIndex = 999;
  editStaffId = 0;
  descSort = ClrDatagridSortOrder.ASC;
  staffForm!: FormGroup;

  constructor(
    private staffService: StaffStoreService,
    private authService: AuthService,
    private alertDispatcher: AlertService,
    private fb: FormBuilder,
    private router: Router
  ) {
    ClarityIcons.addIcons(twoWayArrowsIcon, pencilIcon, floppyIcon, timesIcon, infoCircleIcon);
    this.staffForm = this.fb.group({
      firstname: ['', [Validators.required]],
      lastname: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
      pps_id: [''],
      crm_id: [''],
      active: [true],
      is_admin: [false],
      crm_username: [''],
      auth_password: ['']
    });
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.isAdmin();
    if (!this.isAdmin) {
      this.alertDispatcher.show({
        message: 'Admin-Rechte erforderlich.',
        clrAlertType: IAlertType.WARNING
      }, 4000);
      this.router.navigate(['/settings']).catch(() => {});
      return;
    }
    this.loadStaff();
  }

  loadStaff() {
    this.loading = true;
    this.staffService.getStaff().subscribe({
      next: (res: WebserviceResponse<Staff[]>) => {
        this.staff = res?.object ?? [];
        this.loadAuthUsers();
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  loadAuthUsers() {
    this.staffService.getAuthUsers().subscribe({
      next: (users: AuthUser[]) => {
        this.authUsersByStaffId = {};
        users.forEach((u) => {
          if (typeof u.staff_id === 'number') {
            this.authUsersByStaffId[u.staff_id] = u;
          }
        });
        this.staff = this.staff.map((s) => ({
          ...s,
          auth_user_id: this.authUsersByStaffId[s.staff_id]?.id ?? null,
          is_admin: this.authUsersByStaffId[s.staff_id]?.is_admin ?? false
        }));
      },
      error: () => {
        this.alertDispatcher.show({
          message: 'Auth-User konnten nicht geladen werden. Admin-Status eventuell veraltet.',
          clrAlertType: IAlertType.WARNING
        }, 5000);
      }
    });
  }

  addStaff() {
    if (this.staffForm.invalid) return;
    const authPassword = (this.staffForm.value.auth_password ?? '').toString().trim();
    const rmsPayload = {
      firstname: this.staffForm.value.firstname,
      lastname: this.staffForm.value.lastname,
      email: this.staffForm.value.email,
      phone: this.staffForm.value.phone,
      pps_id: this.staffForm.value.pps_id,
      crm_id: this.staffForm.value.crm_id,
      active: this.staffForm.value.active
    };

    this.staffService.createStaff(rmsPayload).pipe(
      switchMap((res) => {
        if (res?.hasErrors) {
          return of(null);
        }
        const created = res?.object?.[0];
        if (!created) {
          return of(null);
        }
        if (!authPassword) {
          this.alertDispatcher.show({
            message: 'Mitarbeiter angelegt, aber kein Auth-User erstellt (Initial-Passwort fehlt).',
            clrAlertType: IAlertType.WARNING
          }, 6000);
          return of(created);
        }

        const displayName = `${created.firstname} ${created.lastname}`.trim();
        const crmUsername = (this.staffForm.value.crm_username ?? '').trim() || null;
        return this.staffService.createAuthUser({
          email: created.email,
          password: authPassword,
          display_name: displayName,
          crm_username: crmUsername,
          is_active: created.active ?? true,
          is_admin: this.staffForm.value.is_admin ?? false,
          staff_id: created.staff_id
        }).pipe(
          map(() => created),
          catchError(() => {
            this.alertDispatcher.show({
              message: 'Mitarbeiter angelegt, aber Auth-User konnte nicht erstellt werden.',
              clrAlertType: IAlertType.WARNING
            }, 6000);
            return of(created);
          })
        );
      })
    ).subscribe({
      next: (created) => {
        if (!created) return;
        this.alertDispatcher.show({
          message: 'Mitarbeiter wurde angelegt.',
          clrAlertType: IAlertType.SUCCESS
        }, 3000);
        this.staffForm.reset({
          firstname: '',
          lastname: '',
          email: '',
          phone: '',
          pps_id: '',
          crm_id: '',
          active: true,
          is_admin: false,
          crm_username: '',
          auth_password: ''
        });
      },
      complete: () => {
        this.loadStaff();
      }
    });
  }

  updateMode(index: number, staffId: number) {
    this.updateIndex = index;
    this.updating = true;
    this.editStaffId = staffId;
  }

  onUpdated(_: boolean) {
    this.updating = false;
    this.updateIndex = 999;
    this.loadStaff();
  }

  deleteStaff(staffId: number, name: string) {
    if (!confirm(`Mitarbeiter ${name} deaktivieren?`)) return;
    this.staffService.deleteStaff(staffId).subscribe({
      next: (res) => {
        if (res?.hasErrors) return;
        const authUser = this.authUsersByStaffId[staffId];
        if (authUser?.id) {
          this.staffService.updateAuthUser(authUser.id, { is_active: false }).subscribe({
            error: () => {
              this.alertDispatcher.show({
                message: 'Mitarbeiter deaktiviert, aber Auth-User konnte nicht deaktiviert werden.',
                clrAlertType: IAlertType.WARNING
              }, 5000);
            }
          });
        }
        this.alertDispatcher.show({
          message: 'Mitarbeiter wurde deaktiviert.',
          clrAlertType: IAlertType.INFO
        }, 3000);
      },
      complete: () => this.loadStaff()
    });
  }
}
